import { ReactNode, useEffect, useRef, useCallback } from 'react';

/**
 * The design's dialog (OO5c).
 *
 * ⭐ **A dialog is a card that floats.** It reuses the card's language exactly - white, a 1px
 * #E5E5E5 hairline, 6px corners (`rounded-xl` is 6px under the OO1 tokens), the 15px semibold
 * head on an #EFEFEF rule - and adds only what being above the page requires: a scrim and a
 * shadow. The sweep (OO6) took at-rest shadows OFF cards and left them ON overlays for this
 * reason; the shadow is what says "above", so here it stays.
 *
 * Before this, every modal drew its own chrome. The three the backlog named still carried the
 * pre-OO1 look - 12px corners, `shadow-xl`, `text-xl font-bold` titles - because a sweep over
 * palette classes cannot see shape.
 *
 * ⭐ THE SCROLL CHAIN IS DELIBERATE (MM1). The panel is a flex column with a `max-h-[90vh]`, and
 * the BODY is the scroll container (`flex-1 min-h-0 overflow-y-auto`). Head and footer stay put,
 * so a long form never scrolls its own title or its Save button out of reach. A `max-h` with the
 * `overflow-y-auto` on some other element is the MM1 defect: with only a maximum, an unbounded
 * child is as tall as its content and nothing scrolls at all.
 *
 * ⚠️ `min-w-0` ON THE PANEL IS NOT DECORATION. The panel is a flex ITEM, and a flex item's
 * default `min-width: auto` refuses to shrink below its content's intrinsic minimum - so ONE
 * unshrinkable child (a date input, a 2-column grid) pushes the whole dialog wider than the
 * viewport. Measured at 250px, the narrowest width Joseph's pane runs: the CAM dialog was 267px
 * wide in a 218px slot and overflowed the PAGE by 48px. With `min-w-0` the panel shrinks and the
 * body scrolls sideways instead.
 *
 * ⚠️ NOT PORTALLED, on purpose. Each dialog renders where its page renders it, and the layer is a
 * prop, so the existing stacking (the A5 duplicate prompt at z-60 sitting on top of an open Add
 * Property at z-50) keeps working exactly as it did.
 */
interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Optional line under the title, in the card-description treatment. */
  description?: ReactNode;
  /** Right-aligned action row on a top rule. Omit for a dialog with no actions. */
  footer?: ReactNode;
  /** When given, the body and footer are wrapped in a <form> and this runs on submit. */
  onSubmit?: (e: React.FormEvent) => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** The stacking layer. Default `z-50`; pass e.g. `z-[1200]` to keep an existing one. */
  zClassName?: string;
  /** Set false for a dialog that must not be dismissed by the scrim or Escape. */
  dismissible?: boolean;
  /**
   * UI-GLASS1 (the 132nd): `choices` is iOS 26's menu - a frosted panel with big rounded corners and no rules - for a
   * pop-up that offers a SHORT LIST OF CHOICES (its rows are `ChoiceRow`). ⛔ Everything else is `card`, the default:
   * his ask was those pop-ups only, "I don't want it to look all weird."
   */
  look?: 'card' | 'choices';
  children?: ReactNode;
}

const lookStyles = {
  card: {
    scrim: 'bg-black/50',
    panel: 'bg-white border border-gray-200 rounded-xl shadow-lg',
    head: 'border-b border-gray-150',
    body: 'px-5 py-5',
    foot: 'border-t border-gray-150',
  },
  choices: {
    scrim: 'bg-black/25 backdrop-blur-sm',
    panel: 'bg-white/75 backdrop-blur-2xl border border-white/60 rounded-[28px] shadow-[0_18px_60px_rgba(0,0,0,0.28)]',
    head: '',
    body: 'px-4 pt-1 pb-4',
    foot: '',
  },
};

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export const Dialog = ({
  open,
  onClose,
  title,
  description,
  footer,
  onSubmit,
  size = 'lg',
  zClassName = 'z-50',
  dismissible = true,
  look = 'card',
  children,
}: DialogProps) => {
  const styles = lookStyles[look];
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  const close = useCallback(() => { if (dismissible) onClose(); }, [dismissible, onClose]);

  // Escape closes; the page behind does not scroll while a dialog is up; focus goes to the
  // panel on open and back to whatever opened it on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      const back = restoreFocusTo.current;
      if (back instanceof HTMLElement && document.contains(back)) back.focus();
    };
  }, [open, close]);

  if (!open) return null;

  const body = (
    <>
      <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-auto ${styles.body}`}>{children}</div>
      {footer && (
        <div className={`flex flex-wrap justify-end gap-3 px-5 py-4 ${styles.foot}`}>
          {footer}
        </div>
      )}
    </>
  );

  return (
    <div className={`fixed inset-0 ${zClassName} flex items-center justify-center p-4`}>
      <div
        className={`absolute inset-0 ${styles.scrim}`}
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        data-look={look}
        className={`relative w-full min-w-0 ${sizeStyles[size]} max-h-[90vh] flex flex-col ${styles.panel} outline-none`}
      >
        <div className={`flex items-start justify-between gap-3 px-5 py-4 ${styles.head}`}>
          <div className="min-w-0">
            <h2 className="text-[15px] leading-tight font-semibold text-gray-900 break-words">{title}</h2>
            {description && <p className="text-[11.5px] text-gray-400 mt-1">{description}</p>}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="-mr-1 -mt-0.5 shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {onSubmit
          ? <form onSubmit={onSubmit} className="flex flex-1 min-h-0 flex-col">{body}</form>
          : body}
      </div>
    </div>
  );
};
